import styled from 'styled-components';
import tw from 'twin.macro';
import {
  Button,
  Select,
  TooltipInfo,
  TooltipReferencePrice,
} from '@/components';
import strings from '@/language/Swedish';
import {
  ButtonColor,
  ButtonSize,
  Icons,
  TooltipColor,
  ColumnChoiceType
} from '@/enums';
import { FC, useEffect } from 'react';
import ColumnChoiceDialog from './ColumnChoiceDialog';
import { ColumnType } from '@/types/dataTypes';
import { defaultColumnOptions } from '@/context/default';

import {
  defaultSoldCoOperative,
  defaultSoldLittleHouse,
  defaultSoldTableAdInfoColumns,
  defaultSoldTableRealEstateColumns,
  defaultSoldTableSaleInfoColumns,
} from '@/context/SoldDefault';
import { defaultAdsTableColumns } from '@/context/AdsDefault';
import { getSelectedColumnOptions } from './table/helpers';

const StyledTableHeader = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-4 p-2 mb-3 border-b border-dashed border-slate-300/50`}
`;

const ItemsLeft = styled.div`
  ${tw`flex-1 flex flex-wrap items-center justify-start gap-2 text-xs`}
`;

const ReverencePrice = styled.div`
  ${tw`flex flex-col sm:flex-row gap-2`}
`;

const Text = styled.span`
  ${tw`hidden ml-2 text-slate-600 sm:inline-block`}
`;

const ItemsRight = styled.div`
  ${tw`flex items-center justify-end gap-2 pr-10`}
`;

const limitOptionsBasic = [
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '15', value: '15' },
  { label: '20', value: '20' },
  { label: '25', value: '25' },
];

const limitOptionsAuthorized = [
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '15', value: '15' },
  { label: '20', value: '20' },
  { label: '25', value: '25' },
  { label: '30', value: '30' },
  { label: '40', value: '40' },
  { label: '50', value: '50' },
  { label: '75', value: '75' },
  { label: '100', value: '100' },
  { label: '150', value: '150' },
  { label: '250', value: '250' },
];

interface Props {
  limit: number | null;
  hasExcludedIds: boolean;
  nrOfObjects?: number;
  nrOfSelectedObjects?: number;
  userRefSqm: number | null;
  referencePrice?: { sqmPrice: number | null; sqmPriceSelected: number | null };
  columns: ColumnType[];
  isAuthorized: boolean;
  hasColumnOption?: boolean;
  resetExcludedIds: () => void;
  handleLimitChange: (nrState: string | number) => void;
  setColumns: (columns: ColumnType[]) => void;
  toggleColumns: (id: string) => void;
  setColumnOption: (value: string | number) => void;
  columnOption: string | number;
  own_choice_reference_fields: any;
}

const TableHeader: FC<Props> = ({
                                  limit,
                                  hasExcludedIds,
                                  nrOfObjects,
                                  nrOfSelectedObjects,
                                  referencePrice,
                                  userRefSqm,
                                  columns,
                                  isAuthorized,
                                  hasColumnOption,
                                  handleLimitChange,
                                  resetExcludedIds,
                                  setColumns,
                                  toggleColumns,
                                  setColumnOption,
                                  columnOption,

                                }) => {
  const limitOptions = isAuthorized
      ? limitOptionsAuthorized
      : limitOptionsBasic;
  const getLimitOption = () => {
    switch (limit) {
      case 5:
        return limitOptions[0];
      case 10:
        return limitOptions[1];
      case 15:
        return limitOptions[2];
      case 20:
        return limitOptions[3];
      case 25:
        return limitOptions[4];
      case 30:
        return limitOptions[5];
      case 40:
        return limitOptions[6];
      case 50:
        return limitOptions[7];
      case 75:
        return limitOptions[8];
      case 100:
        return limitOptions[9];
      case 150:
        return limitOptions[10];
      case 250:
        return limitOptions[11];
      default:
        return limitOptions[2];
    }
  };
  const hasRefPrice = () => {
    if (userRefSqm && userRefSqm !== 0) return true;

    return false;
  };

  const hasSelectedRefPrice = () => {
    if (
        referencePrice?.sqmPriceSelected &&
        nrOfSelectedObjects &&
        nrOfSelectedObjects !== 0
    )
      return true;

    return false;
  };
  const getColumnsFromOption = (value?: string): ColumnType[] => {
    let columnChoice: ColumnType[] = defaultAdsTableColumns;
    if (value === ColumnChoiceType.sold)
      columnChoice = defaultSoldTableSaleInfoColumns;
    else if (value === ColumnChoiceType.ads)
      columnChoice = defaultSoldTableAdInfoColumns;
    else if (value === ColumnChoiceType.realEstate)
      columnChoice = defaultSoldTableRealEstateColumns;
    else if (value === ColumnChoiceType.coOperative)
      columnChoice = defaultSoldCoOperative;
    else if (value === ColumnChoiceType.littleHouse)
      columnChoice = defaultSoldLittleHouse;
    else if (value === ColumnChoiceType.ownChoice) {
      columnChoice = getSelectedColumnOptions();
    }
    else columnChoice = defaultAdsTableColumns;

    let columnChoiceIds;
    if (value === ColumnChoiceType.ownChoice) {
      columnChoiceIds = columnChoice.filter((column) => column.isVisible).map(column => column.id);
    } else {
      columnChoiceIds = columnChoice.map(column => column.id);
    }

    return columns.map(({ id, label }) => ({
      id,
      isVisible: columnChoiceIds.includes(id),
      label,
    }));
  };

  useEffect(() => {
    const ble = getColumnsFromOption(columnOption as string)
    setColumns(ble);
  },[columnOption]);

  return (
      <StyledTableHeader>
        <ItemsLeft>
          <Select
              selectedValue={getLimitOption().value}
              options={limitOptions}
              handleChange={(value) => {
                handleLimitChange(value);
              }}
          />
          {hasRefPrice() && (
              <ReverencePrice>
                {referencePrice?.sqmPrice && (
                    <Text>{strings.data.referencePrice}:</Text>
                )}
                {userRefSqm && referencePrice?.sqmPrice && (
                    <TooltipReferencePrice
                        color={TooltipColor.secondary}
                        content={`${strings.priceDevelopment.refPriceBasedOn} ${nrOfObjects} ${strings.priceDevelopment.hits} ${strings.priceDevelopment.moreInfo}`}
                        referencePrice={referencePrice.sqmPrice * userRefSqm}
                        referencePriceSqm={referencePrice.sqmPrice}
                    />
                )}
                {hasSelectedRefPrice() && userRefSqm && (
                    <TooltipReferencePrice
                        color={TooltipColor.primary}
                        content={`${strings.priceDevelopment.refPriceBasedOn} ${nrOfSelectedObjects} ${strings.priceDevelopment.selectedObjects}`}
                        referencePrice={
                            userRefSqm * (referencePrice?.sqmPriceSelected || 0)
                        }
                        referencePriceSqm={referencePrice?.sqmPriceSelected}
                    />
                )}
              </ReverencePrice>
          )}
        </ItemsLeft>
        <ItemsRight>
          {hasColumnOption && (
              <Select
                  selectedValue={columnOption}
                  options={defaultColumnOptions}
                  handleChange={(value) => {
                    setColumnOption(value);
                    setColumns(getColumnsFromOption(columnOption as string));
                  }}
              />
          )}
          <ColumnChoiceDialog columns={columns} toggleColumn={toggleColumns} columnOption={columnOption} />
          <TooltipInfo content={strings.table.showHiddenRows}>
            <div>
              <Button
                  icon={hasExcludedIds ? Icons.eyeClosed : Icons.eye}
                  color={ButtonColor.border}
                  size={ButtonSize.medium}
                  onClick={resetExcludedIds}
              />
            </div>
          </TooltipInfo>
        </ItemsRight>
      </StyledTableHeader>
  );
};

export default TableHeader;